import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const InviteUserDialog = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [accessLevel, setAccessLevel] = useState("full_access");
  const [customMessage, setCustomMessage] = useState("");

  const handleInvite = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-user-invite', {
        body: { email: email.trim().toLowerCase(), accessLevel, customMessage }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`Invitation sent successfully to ${email}!`);
        setEmail("");
        setCustomMessage("");
        setAccessLevel("full_access");
        setOpen(false);
      } else {
        toast.error(data.error || "Failed to send invitation");
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error("Failed to send invitation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <UserPlus className="w-4 h-4" />
          Invite User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite New User</DialogTitle>
          <DialogDescription>
            Send an invitation to manually add a user to the LaunchToRevenue platform.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="accessLevel">Access Level *</Label>
            <Select value={accessLevel} onValueChange={setAccessLevel} disabled={loading}>
              <SelectTrigger id="accessLevel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_access">Full Access - Complete dashboard and community access</SelectItem>
                <SelectItem value="community_only">Community Only - Access to Skool community only</SelectItem>
                <SelectItem value="dashboard_only">Dashboard Only - Access to dashboard tools only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customMessage">Custom Message (Optional)</Label>
            <Textarea
              id="customMessage"
              placeholder="Add a personal welcome message..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              disabled={loading}
              rows={4}
            />
          </div>

          <div className="bg-muted p-3 rounded-md text-sm">
            <p className="font-medium mb-1">📧 What happens next:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>User receives an email with a unique invitation link</li>
              <li>Link expires in 7 days</li>
              <li>User creates their account with the selected access level</li>
              <li>You can track invitation status in the system</li>
            </ul>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={loading}>
            {loading ? "Sending..." : "Send Invitation"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
